import type { Service } from '../types';

export const servicesEn: Service[] = [
  {
    id: "video-produksiyon",
    slug: "video-produksiyon",
    title: "Film & Video Production",
    metaTitle: "Commercial Film & Video Production Agency - WE24 Agency Istanbul",
    metaDescription: "With over 15 years of on-set mastery, we produce cinema-grade commercial films, industrial factory showcases, documentaries, music videos, and licensed 5.1K aerial cinematography.",
    keywords: [
      "commercial film production istanbul",
      "video production company turkey",
      "factory promotional film",
      "aerial drone filming istanbul",
      "music video production",
      "360 vr video agency",
      "industrial film director"
    ],
    shortDescription: "Translating vision into cinema: we produce commercials, corporate showcases, industrial factory films, documentaries, and music videos with uncompromising craft.",
    fullDescription: "At WE24 Agency, backed by 15+ years of live set and cinema production experience, we articulate your brand’s authority, engineering scale, and narrative through evocative cinematography. From concept development, scriptwriting, and talent casting to DGCA-licensed 5.1K aerial drone flights and high-end cinema camera rigs (RED, ARRI, Sony FX), our in-house directors and technical crew oversee every phase of production.",
    subservices: [
      "Corporate & Industrial Factory Promotional Films",
      "Broadcast TV & High-Impact Digital Commercials",
      "Music Video Clip & Live Performance Production",
      "Licensed Aerial 5.1K / 4K Drone Cinematography",
      "360-Degree Spherical Video & Immersive VR/AR",
      "Global Summit, Exhibition & Event Aftermovies"
    ],
    subserviceItems: [
      {
        title: "Corporate & Factory Promotional Films",
        slug: "tanitim-filmi",
        description: "Prestigious cinematic showcases highlighting robotic manufacturing lines, cleanrooms, engineering quality standards, and global vision for B2B export and investors.",
        keywords: ["factory promotional video", "industrial film production", "corporate video istanbul"]
      },
      {
        title: "Broadcast TV & Digital Commercials",
        slug: "reklam-filmi",
        description: "High-concept, high-production-value commercials engineered for prime-time national television networks and targeted digital advertising funnels (YouTube, Meta, TikTok).",
        keywords: ["tv commercial production", "digital ad video", "creative advertising video"]
      },
      {
        title: "Music Video Clip Production",
        slug: "muzik-video-klip",
        description: "Artistic vision, dynamic lighting design, and rhythmic editing crafted for record labels and solo artists seeking international visual resonance.",
        keywords: ["music video shoot istanbul", "music clip director", "music video production"]
      },
      {
        title: "Licensed Aerial Drone Cinematography",
        slug: "havadan-drone-video",
        description: "Commercially licensed DGCA drone pilots operating 5.1K/4K cinema drones for sweeping aerial perspectives across industrial complexes, architecture, and landscapes.",
        keywords: ["drone video filming", "aerial cinematography", "commercial drone pilot turkey"]
      },
      {
        title: "360-Degree Video & Immersive VR / AR",
        slug: "360-vr-video",
        description: "Interactive spherical 360-degree video capture, virtual industrial tours, and spatial augmented reality assets designed for global trade expos and digital showcases.",
        keywords: ["360 degree video production", "virtual tour video", "vr video agency"]
      },
      {
        title: "Event & Launch Aftermovies",
        slug: "etkinlik-aftermovie",
        description: "Kinetic highlight films capturing the momentum, keynote addresses, and high energy of global summits, product launches, and award galas.",
        keywords: ["event aftermovie", "summit video coverage", "product launch film"]
      }
    ],
    deliverables: [
      "4K / 8K Cinema Master Video Files (ProRes 422 HQ / 4444 XQ)",
      "Multi-Format Social Media Deliverables (9:16 Vertical Reels/Shorts, 1:1 Square, 16:9 Landscape)",
      "15s / 30s High-Velocity Teaser & Ad Cutdowns",
      "High-Resolution Set & Production Photography Archive",
      "Multilingual Native Voiceover & Localized Subtitle Master Packages"
    ],
    process: [
      "Strategic Briefing: Analyzing core brand objectives, audience demographics, and distribution channels.",
      "Creative Script & Storyboard: Developing visual treatments, shot lists, lighting schemes, and narrative arcs.",
      "Pre-Production & Logistics: Location scouting, municipal permits, talent casting, and crew scheduling.",
      "Principal Photography: On-set filming directed by seasoned cinematographers with dedicated camera and lighting crews.",
      "Post-Production & Mastering: Offline editing, DaVinci Resolve color grading, sound design, and master delivery."
    ],
    techSpecs: [
      {
        category: "Cinema Cameras & Optics",
        items: ["RED Digital Cinema, ARRI & Sony FX Cinema Camera Rigs", "Prime Cine Lens Sets & Anamorphic Glass", "Zero-Delay Teradek Wireless Video Monitoring"]
      },
      {
        category: "Lighting & Grip Systems",
        items: ["Aputure & ARRI Professional LED & HMI Staging Kits", "DJI Ronin Gimbals, Heavy-Duty Dolly & Steadicam Systems", "DGCA-Certified 5.1K/4K Cinema Drone Fleet"]
      },
      {
        category: "Audio Capture",
        items: ["Sennheiser & Rode Broadcast Wireless Lavalier Systems", "Boom Microphones & 32-Bit Float Multi-Channel Recorders"]
      }
    ],
    articleSections: [
      {
        heading: "Why High-End Film Production Drives Market Leadership",
        content: "In an era of generic digital noise, cinematic video remains the undisputed medium for establishing immediate credibility and enterprise authority. Rather than relying on sterile stock footage, bespoke films featuring your genuine facilities, workforce, and technological capabilities establish an unassailable leadership presence in the minds of prospective clients and global partners.",
        subPoints: [
          "Multiplies Conversion Velocity: Landing pages and sales presentations with custom brand films accelerate buying decisions by up to 80%.",
          "Tangible Enterprise Prestige: Uncompromising cinematography serves as direct proof of your operational excellence.",
          "Global Export Readiness: Localized native voiceovers and multi-language subtitle tracks empower international B2B trade delegations."
        ]
      },
      {
        heading: "Industrial Manufacturing & Factory Tour Filming Expertise",
        content: "We have honed a specialized industrial filming methodology tailored for manufacturing enterprises, exporters, and engineering facilities. Our crew operates in strict compliance with Occupational Health & Safety (OHS) regulations, documenting robotic assembly lines, cleanrooms, and testing facilities without interrupting your production flow."
      }
    ],
    faqs: [
      {
        question: "How long does a corporate promotional film take from initial briefing to final delivery?",
        answer: "Depending on scope and locations, on-set filming typically requires 1 to 3 production days. Following shoots, complete editing, color grading, sound mastering, and revisions are finalized within 10-15 business days."
      },
      {
        question: "Who manages flight permits and legal authorizations for drone filming?",
        answer: "All aerial missions are executed by our commercially licensed DGCA drone pilots. Flight clearances, airspace notifications, and comprehensive third-party liability insurances are managed end-to-end by WE24 Agency."
      },
      {
        question: "Can you provide localized versions with foreign language voiceovers?",
        answer: "Yes; we routinely provide native voiceover artists in English, German, French, Arabic, Russian, and Spanish, accompanied by professionally localized subtitle packages."
      }
    ],
    icon: "Video"
  },
  {
    id: "canli-yayin-program",
    slug: "canli-yayin-program",
    title: "Live Broadcasting & Vision Mixing",
    metaTitle: "Live Stream & Multi-Camera Vision Mixing Services - WE24 Agency Istanbul",
    metaDescription: "Broadcast-grade multi-camera live streaming for product launches, esports tournaments, corporate summits, and hybrid conferences with low-latency bonded cellular failover.",
    keywords: [
      "live broadcast production istanbul",
      "multi camera vision mixing",
      "esports live stream agency",
      "hybrid summit live broadcast",
      "simulcast streaming services",
      "atem vision mixer rental"
    ],
    shortDescription: "Broadcasting product launches, summits, and esports tournaments across global digital platforms with broadcast-level multi-camera systems.",
    fullDescription: "Dissolving geographic boundaries, we broadcast your high-stakes events worldwide in television-standard fidelity. With multi-camera vision mixing, real-time broadcast graphics, simultaneous translation integration, and pro audio balancing, we engineer seamless digital experiences.",
    subservices: [
      "Multi-Camera 4K Live Broadcast & Vision Mixing",
      "Product Launches, Press Conferences & Global Summits",
      "Esports Tournaments & Gaming Broadcast Production",
      "Multi-Platform Simulcast (YouTube, LinkedIn, Twitch, Facebook)",
      "Hybrid Event Infrastructure & Interactive Remote Feeds",
      "TV & Digital Studio Shows / Competition Formats"
    ],
    subserviceItems: [
      {
        title: "Multi-Camera Vision Mixing",
        slug: "reji-canli-yayin",
        description: "ATEM 4K video switchers, wireless broadcast cameras, and real-time live directing for flawless broadcast television quality.",
        keywords: ["vision mixing", "live multicamera production", "broadcast switching"]
      },
      {
        title: "Esports & Gaming Broadcasts",
        slug: "espor-gaming-yayin",
        description: "Field-tested on major tournaments (Kristal Piksel, TaleWorlds); live game HUD integration, player cams, and caster desk setups.",
        keywords: ["esports live stream", "gaming tournament production", "twitch stream setup"]
      },
      {
        title: "Hybrid Summits & Global Conferences",
        slug: "hibrit-etkinlik-zirve",
        description: "Bridging in-person attendees and global remote audiences with two-way low-latency video, real-time polling, and interactive Q&A.",
        keywords: ["hybrid event streaming", "virtual summit broadcast", "zoom webinar integration"]
      },
      {
        title: "Multi-Platform Simulcast",
        slug: "simulcast-yayin",
        description: "Distributing a pristine single feed concurrently to YouTube, LinkedIn Live, X (Twitter), Facebook, and custom web portals.",
        keywords: ["simulcast stream", "multi platform live streaming", "concurrent broadcast"]
      },
      {
        title: "Studio Production & Talk Show Formats",
        slug: "studyo-programciligi",
        description: "Bespoke studio set design, multi-camera direction, teleprompters, and dynamic animated lower-thirds for YouTube and TV broadcasts.",
        keywords: ["studio talk show production", "webcast studio rental", "branded video podcast"]
      }
    ],
    deliverables: [
      "Uninterrupted High-Bitrate Live Stream Feeds (1080p60 / 4K)",
      "Clean & Graphic-Overlay PGM Master Recordings",
      "Real-Time Instant Highlight & Social Media Video Snippets",
      "Comprehensive Post-Event Viewership & Engagement Analytics"
    ],
    process: [
      "Venue Audit & Bandwidth Stress-Test: Inspecting power redundancy, acoustic properties, and upload bandwidth.",
      "Control Room & Camera Staging: Rigging multi-camera positions, vision switchers, audio consoles, and graphics servers.",
      "Rehearsals & Audio Alignment: Conducting closed-circuit dry runs with speakers, title cards, and video inserts.",
      "Live Broadcast Execution: Live directing, audio mixing, and continuous stream monitoring by seasoned engineers.",
      "Master Archiving: Immediate post-event handover of all ISO camera feeds and master recordings."
    ],
    techSpecs: [
      {
        category: "Vision Switchers & Video Routing",
        items: ["Blackmagic Design ATEM 4K Production Switchers", "vMix 4K Dedicated Broadcast Servers", "NDI & SDI Low-Latency Signal Lines"]
      },
      {
        category: "Cameras & Wireless Links",
        items: ["Sony FX9, FX6 & FX3 Cinema Camera Rigs", "PTZ Robotic Remote Cameras", "Teradek Bolt 4K Zero-Delay Wireless Transmitters"]
      },
      {
        category: "Audio & Network Failover",
        items: ["Behringer & Yamaha Digital Audio Consoles", "Dante Digital Audio Networking", "Multi-WAN Bonded 4G/5G Cellular Router Units"]
      }
    ],
    articleSections: [
      {
        heading: "Zero-Downtime Guarantee for High-Stakes Broadcasts",
        content: "Corporate product keynotes, shareholder assemblies, and global summits allow no second takes. WE24 Agency deploys bonded 4G/5G multi-carrier cellular routers, backup generators, and redundant switchers to ensure 100% uninterrupted broadcast uptime.",
        subPoints: [
          "Bonded Cellular Redundancy: Seamless failover across multiple mobile network carriers if local venue internet falters.",
          "Broadcast TV Quality Lower-Thirds: Custom animated badges, speaker titles, live voting tallies, and sponsor overlays.",
          "Interactive Remote Feeds: Integrating VIP remote keynote speakers via ultra-low-latency return links."
        ]
      }
    ],
    faqs: [
      {
        question: "What if our event venue has inadequate or unstable internet?",
        answer: "We deploy our proprietary portable 4G/5G multi-SIM bonding hardware, generating resilient, high-speed upstream connectivity independent of venue network constraints."
      },
      {
        question: "How do you handle simultaneous language interpretation on live streams?",
        answer: "We interface directly with onsite interpretation booths or remote translators, outputting discrete selectable audio tracks across YouTube or specialized event portals."
      }
    ],
    icon: "Radio"
  },
  {
    id: "post-produksiyon",
    slug: "post-produksiyon",
    title: "Post-Production, Color & VFX",
    metaTitle: "Post Production, DaVinci Color Grading & VFX Studio - WE24 Agency",
    metaDescription: "DaVinci Resolve cinematic color grading, advanced visual effects (VFX), 2D/3D motion graphics, professional video editing, and audio mastering.",
    keywords: [
      "post production istanbul",
      "davinci resolve color grading",
      "vfx studio turkey",
      "video editing services",
      "3d animation studio",
      "audio sound design"
    ],
    shortDescription: "DaVinci Resolve color grading, visual effects, 3D motion design, and audio mastering that elevate footage into cinematic art.",
    fullDescription: "Raw footage achieves its full dramatic potential in the post suite. At WE24 Agency, our calibrated reference monitors, high-performance render stations, and master colorists craft Hollywood-caliber visual harmony and impactful visual effects for your footage.",
    subservices: [
      "Offline & Online Professional Video Editing",
      "DaVinci Resolve Cinematic Color Grading",
      "VFX & Visual Effects Design (CGI, Clean-up, Compositing)",
      "2D & 3D Motion Graphics & Animation",
      "Cinematic Sound Design, Foley & Master Audio Mixing"
    ],
    subserviceItems: [
      {
        title: "Professional Editing & Montage",
        slug: "kurgu-montaj",
        description: "Rhythm-driven, immersive narrative editing that sharpens pacing, elevates drama, and commands viewer retention.",
        keywords: ["video editing", "film montage", "commercial video editor"]
      },
      {
        title: "DaVinci Resolve Color Grading",
        slug: "renk-duzenleme",
        description: "Precision Log/RAW grading on Flanders Scientific reference displays, delivering tailored color palettes and filmic skin tones.",
        keywords: ["color grading studio", "davinci resolve colorist", "cinematic look"]
      },
      {
        title: "VFX & Visual Effects",
        slug: "vfx-gorsel-efekt",
        description: "Green screen compositing, object removal/clean-up, digital set extension, and photorealistic CGI integration.",
        keywords: ["vfx studio istanbul", "visual effects company", "cgi compositing"]
      },
      {
        title: "2D / 3D Motion Design",
        slug: "animasyon-motion-graphics",
        description: "3D industrial exploded-view animations, technical product teardowns, kinetic typography, and broadcast-level graphic packages.",
        keywords: ["3d animation studio", "motion design agency", "industrial 3d rendering"]
      },
      {
        title: "Sound Design & Audio Mastering",
        slug: "ses-tasarimi-mastering",
        description: "Bespoke ambient audio landscapes, foley sound effects, dialogue denoising, and EBU R128 broadcast-standard audio mastering.",
        keywords: ["sound design", "audio mixing", "audio mastering"]
      }
    ],
    deliverables: [
      "Rec.709, DCI-P3 & HDR10 Master Color Deliverables",
      "Lossless ProRes 422 HQ / 4444 XQ and H.265 Web Packages",
      "Layered Project Archives & Stems (Dialogue, Music, SFX separated)",
      "Broadcast EBU R128 / Netflix-Compliant Audio Mixes"
    ],
    process: [
      "Data Ingest & Checksum Verification: Secure dual-redundant backup to 10GbE NVMe storage servers.",
      "Offline Rough Cut: Establishing story arcs, pacing, and visual sequences for client review.",
      "Fine Cut & Conform: Finalizing transitions, timing, and narrative flow.",
      "Color Grading & VFX: DaVinci Resolve color passes, visual effects integration, and graphics finishing.",
      "Sound Design & Master Export: Audio denoising, foley layering, leveling, and multi-format master rendering."
    ],
    techSpecs: [
      {
        category: "Hardware Infrastructure",
        items: ["DaVinci Resolve Studio & Blackmagic Advanced Panels", "Flanders Scientific Calibrated Reference Displays", "High-Speed 10GbE NVMe NAS Storage Networks"]
      },
      {
        category: "Software Suite",
        items: ["DaVinci Resolve Studio", "Adobe Creative Cloud (Premiere, After Effects)", "Cinema 4D, Blender & Unreal Engine 5"]
      }
    ],
    articleSections: [
      {
        heading: "The Power of Cinematic Color Grading",
        content: "Color grading is not mere exposure correction; it is visual psychology. With industry-leading color science, we shape emotions, accentuate brand identity, and impart unmistakable cinematic texture into every frame."
      }
    ],
    faqs: [
      {
        question: "Can you provide post-production on footage shot by third parties?",
        answer: "Yes; we routinely accept raw Log and RAW camera files (Sony, RED, ARRI, Blackmagic, Canon) to execute full-pipeline editing, grading, and VFX."
      }
    ],
    icon: "Sparkles"
  },
  {
    id: "fotograf-produksiyon",
    slug: "fotograf-produksiyon",
    title: "Photography Production",
    metaTitle: "Commercial & Architectural Photography - WE24 Agency Istanbul",
    metaDescription: "High-resolution architectural, industrial factory, commercial product, fashion model, and aerial drone photography services.",
    keywords: [
      "commercial photography istanbul",
      "architectural photographer turkey",
      "factory industrial photography",
      "product photography studio",
      "fashion lookbook shoot"
    ],
    shortDescription: "From architectural landmarks and industrial manufacturing plants to studio product shoots and fashion campaigns, we deliver high-resolution imagery.",
    fullDescription: "In visual marketing, first impressions are forged in milliseconds. WE24 Agency’s photography team utilizes high-dynamic-range full-frame and medium format camera bodies, state-of-the-art studio lighting, and high-end retouching to capture your facilities, products, and personnel in prestigious light.",
    subservices: [
      "Architectural & Interior / Exterior Photography",
      "Industrial & Factory Plant Photography",
      "Commercial Product & E-Commerce Shoots",
      "Model, Fashion & Lifestyle Studio Photography",
      "High-Resolution Aerial Drone Photography"
    ],
    subserviceItems: [
      {
        title: "Architectural & Interior Photography",
        slug: "mimari-fotograflama",
        description: "Perspective-corrected tilt-shift captures of luxury hotels, corporate headquarters, and architectural developments.",
        keywords: ["architectural photography", "hotel interior photographer", "facade photography"]
      },
      {
        title: "Industrial & Factory Photography",
        slug: "endustriyel-fotografcilik",
        description: "Catalog-grade imagery showcasing production machinery, cleanrooms, and manufacturing scale for investor and marketing materials.",
        keywords: ["factory photography", "industrial photographer", "manufacturing photo shoot"]
      },
      {
        title: "Product & E-Commerce Shoots",
        slug: "urun-fotografciligi",
        description: "Shadowless pure white background cutouts, conceptual styling, and macro texture captures for cosmetics, luxury goods, and consumer products.",
        keywords: ["product photography", "ecommerce studio shoot", "packshot photography"]
      },
      {
        title: "Fashion & Model Campaigns",
        slug: "model-moda-fotografciligi",
        description: "Lookbooks, lookbook styling, casting direction, and editorial fashion photography in studio or on location.",
        keywords: ["fashion photography", "model lookbook shoot", "apparel catalog photos"]
      },
      {
        title: "Aerial Drone Photography",
        slug: "havadan-drone-fotograf",
        description: "High-resolution RAW aerial perspectives and orthophoto mapping for expansive industrial sites and real estate assets.",
        keywords: ["aerial drone photography", "drone real estate photos", "facility aerial shot"]
      }
    ],
    deliverables: [
      "45-60 Megapixel Master RAW & 16-bit TIFF Files",
      "Optimized WebP and JPEG Files for Fast Digital Loading",
      "Commercial High-End Color-Accurate Retouching",
      "Pure White & Transparent PNG Cutout Files for E-Commerce"
    ],
    process: [
      "Concept & Moodboard: Designing lighting schemes, color harmony, and shot lists.",
      "Studio & Staging Setup: Product prepping, set dressing, and precision strobe alignment.",
      "Tethered Live Capture: Direct real-time computer monitor preview for immediate client sign-off.",
      "High-End Retouching: Surface cleaning, skin smoothing, and micro-contrast adjustments.",
      "Multi-Format Delivery: Exporting full-resolution print masters and web-ready compressed assets."
    ],
    techSpecs: [
      {
        category: "Camera & Optics",
        items: ["Sony Alpha High-Resolution Full-Frame Bodies", "Sony G-Master & Canon L-Series Prime & Tilt-Shift Lenses", "Hasselblad Medium Format Studio Systems"]
      },
      {
        category: "Lighting & Modifiers",
        items: ["Profoto & Broncolor Professional Monolights", "Assorted Softboxes, Octas & Hard Reflectors", "Tether Tools High-Speed Live Capture Hardware"]
      }
    ],
    articleSections: [
      {
        heading: "How High-Fidelity Imagery Drives Sales Conversion",
        content: "In digital commerce, visual fidelity is the primary proxy for quality. Crisp, color-calibrated product photography eliminates purchase friction, reduces return rates by up to 40%, and substantially increases checkout conversion."
      }
    ],
    faqs: [
      {
        question: "Can you execute photography shoots on location at our facility?",
        answer: "Yes; we regularly deploy mobile studio strobe setups and tethered stations directly to corporate offices, manufacturing plants, and showrooms across Turkey and internationally."
      }
    ],
    icon: "Camera"
  },
  {
    id: "dijital-ve-sosyal-medya",
    slug: "dijital-ve-sosyal-medya",
    title: "Digital Strategy & Social Media",
    metaTitle: "Social Media Management & Digital Strategy - WE24 Agency",
    metaDescription: "Data-driven social media management, vertical viral video production (Reels/TikTok), Meta & Google Ads performance marketing, and YouTube SEO.",
    keywords: [
      "social media agency istanbul",
      "social media management turkey",
      "reels tiktok video production",
      "meta ads management",
      "youtube seo agency"
    ],
    shortDescription: "Driving growth through social media management, high-engagement short-form video production, and performance-driven paid acquisition.",
    fullDescription: "We rethink conventional social media agency playbooks. By integrating our video production heritage into digital strategy, we deliver high-impact vertical video (Reels, TikTok, Shorts), magnetic visual identities, and data-backed paid performance campaigns that maximize your revenue.",
    subservices: [
      "Social Media Management & Editorial Content Planning",
      "Dynamic Vertical Video Production (Instagram Reels / TikTok)",
      "Meta (Instagram & Facebook), TikTok & Google Ads Management",
      "YouTube Channel Strategy, Video SEO & Audience Growth",
      "Influencer Marketing & Brand Partnerships",
      "Community Management & Online Reputation Control"
    ],
    subserviceItems: [
      {
        title: "Social Media Account Management",
        slug: "sosyal-medya-hesap-yonetimi",
        description: "Monthly strategic editorial calendars, bespoke graphic templates, authoritative copywriting, and proactive community engagement.",
        keywords: ["social media management", "instagram account management", "social media strategy"]
      },
      {
        title: "Dynamic Short-Form Video (Reels / TikTok)",
        slug: "dijital-icerik-uretimi",
        description: "Algorithm-favored, hook-driven short-form video productions engineered to capture viewer curiosity in the first 3 seconds.",
        keywords: ["reels production", "tiktok video creation", "viral short form video"]
      },
      {
        title: "Performance & Social Advertising",
        slug: "sosyal-medya-reklam-yonetimi",
        description: "ROAS-centric campaign management on Meta Ads Manager and Google Ads with relentless A/B testing and funnel optimization.",
        keywords: ["meta ads agency", "paid social advertising", "performance marketing istanbul"]
      },
      {
        title: "YouTube Channel Management & SEO",
        slug: "youtube-kanal-yonetimi-seo",
        description: "High-CTR custom thumbnail creation, keyword tagging, playlist architecture, and long-tail video search optimization.",
        keywords: ["youtube seo", "youtube channel optimization", "video audience growth"]
      }
    ],
    deliverables: [
      "Approved Monthly Content & Production Calendar",
      "Weekly High-Resolution Video Batches (Reels / TikTok)",
      "Ad Campaign Setup, Audience Retargeting & Daily Optimization",
      "Comprehensive Monthly Growth, Engagement & ROAS Reports"
    ],
    process: [
      "Competitive Audit: Examining industry benchmarks, competitor creative patterns, and target persona interests.",
      "Creative Concepting: Formulating visual frameworks, post frequencies, and viral video themes.",
      "Monthly Shoot Cycles: Our camera crews film monthly asset batches in our studio or at your location.",
      "Publishing & Paid Media: Launching approved creative assets and optimizing ad budgets daily.",
      "Analytics & Iteration: Reviewing performance telemetry to refine the subsequent month’s roadmap."
    ],
    techSpecs: [
      {
        category: "Analytics & Management Stack",
        items: ["Meta Business Suite & Google Analytics 4", "Semrush, TubeBuddy & VidIQ Video SEO", "Sprout Social & Hootsuite Listening Tools"]
      }
    ],
    articleSections: [
      {
        heading: "Why Creative Quality Determines Advertising ROAS",
        content: "Over 70% of an ad campaign’s return on ad spend (ROAS) is dictated by creative quality. Technically flawless targeting paired with dull assets burns ad budgets. WE24 Agency produces captivating creative assets that lower cost-per-click (CPC) and drive real pipeline revenue."
      }
    ],
    faqs: [
      {
        question: "How frequently do you produce new content for our channels?",
        answer: "We typically conduct monthly or bi-weekly dedicated filming days, yielding 15 to 30 custom video assets and carousel designs distributed systematically throughout the month."
      }
    ],
    icon: "Share2"
  },
  {
    id: "tasarim-ve-marka",
    slug: "tasarim-ve-marka",
    title: "Brand Identity & Graphic Design",
    metaTitle: "Corporate Brand Identity & Graphic Design - WE24 Agency",
    metaDescription: "Logo design, corporate identity systems, brand guideline manuals (Brandbooks), catalog & editorial publication design, and packaging.",
    keywords: [
      "brand identity agency istanbul",
      "logo design studio",
      "corporate brandbook creation",
      "catalog layout design",
      "packaging design turkey"
    ],
    shortDescription: "Crafting timeless logos, corporate design systems, editorial publications, packaging, and digital UI/UX frameworks.",
    fullDescription: "A brand’s visual signature is its indelible mark on the world. From typography and chromatic palettes to logo geometry, packaging tactile qualities, and editorial layouts, we construct coherent, prestigious design systems that communicate your values effortlessly.",
    subservices: [
      "Corporate Brand Identity & Logo Design",
      "Social Media Visual Design Systems",
      "Catalog, Magazine & Editorial Layout Design",
      "Packaging Design & Product Dressing",
      "Exhibition Booth & Environmental Graphics",
      "UI / UX Design Systems & Web Interfaces"
    ],
    subserviceItems: [
      {
        title: "Corporate Identity & Logo Design",
        slug: "kurumsal-kimlik-tasarimi",
        description: "Distinctive logo marks, corporate typography, color palettes, stationery suites, and an exhaustive Brandbook manual.",
        keywords: ["logo design", "brand identity manual", "corporate brandbook"]
      },
      {
        title: "Social Media Design Systems",
        slug: "sosyal-medya-tasarimi",
        description: "Cohesive, modern visual templates for Instagram, LinkedIn, and corporate communication channels.",
        keywords: ["social media design", "instagram templates", "brand visual system"]
      },
      {
        title: "Catalog & Editorial Layout (Print & PDF)",
        slug: "dergi-sayfa-tasarimi-mizanpaj",
        description: "International-standard page layouts for luxury product catalogs, corporate annual reports, and prestige magazines.",
        keywords: ["catalog design", "editorial layout", "annual report design"]
      },
      {
        title: "Packaging & Label Design",
        slug: "ambalaj-tasarimi",
        description: "Shelf-stopping packaging solutions engineered in close alignment with material substrates and industrial printing specs.",
        keywords: ["packaging design", "label design", "custom box packaging"]
      }
    ],
    deliverables: [
      "Vector Logo Master Packages (AI, EPS, SVG, PDF, PNG)",
      "Comprehensive Corporate Brand Guidelines Manual (PDF)",
      "Print-Ready High-Resolution Pre-Press Files (CMYK + Dielines)",
      "Digital RGB Typography & Web Asset Kits"
    ],
    process: [
      "Brand Positioning & Discovery: Investigating brand core values, market landscape, and audience perception.",
      "Ideation & Sketching: Exploring distinctive conceptual avenues with our creative team.",
      "Concept Presentation: Presenting curated design routes staged in real-world application mockups.",
      "Refinement & Polish: Fine-tuning chosen concepts based on structured feedback.",
      "Master Asset Archiving: Delivering all print and digital source files in an organized repository."
    ],
    techSpecs: [
      {
        category: "Design Software Suite",
        items: ["Adobe Illustrator & Photoshop", "Adobe InDesign (Editorial Layout)", "Figma (Design Systems & UI/UX)"]
      }
    ],
    articleSections: [
      {
        heading: "The Business Value of Design Consistency",
        content: "A disciplined, elegant corporate identity elevates perceived enterprise valuation. Cohesive visual branding instills investor confidence, supports premium pricing power, and fosters enduring customer loyalty."
      }
    ],
    faqs: [
      {
        question: "What assets are included in our brand identity deliverables?",
        answer: "You receive vector source files (AI, EPS, PDF), transparent PNGs, font licenses, CMYK/RGB/Pantone/HEX color values, and a complete Brandbook guideline manual."
      }
    ],
    icon: "Palette"
  },
  {
    id: "web-ve-yazilim",
    slug: "web-ve-yazilim",
    title: "Web Development & Custom Software",
    metaTitle: "Modern Web Design & Custom Software Engineering - WE24 Agency",
    metaDescription: "Ultra-fast, SEO-optimized corporate websites, Next.js / Vite web applications, e-commerce architectures, and bespoke software solutions.",
    keywords: [
      "web development agency istanbul",
      "custom software engineering",
      "corporate website design",
      "nextjs react web development",
      "headless cms development",
      "ecommerce platform development"
    ],
    shortDescription: "Ultra-fast, SEO-dominant, conversion-engineered digital web experiences built with modern React, Next.js, and Vite architectures.",
    fullDescription: "We engineer digital platforms that combine striking aesthetics with 100/100 Google Core Web Vitals performance. Replacing slow, vulnerable template frameworks with tailored React, Next.js, and TypeScript architectures, we deliver bulletproof web solutions engineered for maximum search visibility and conversion.",
    subservices: [
      "Modern Corporate Web Design & Development",
      "Custom Web Applications & SaaS Platform Engineering",
      "E-Commerce Architecture & Payment Gateways Integration",
      "Mobile-First Responsive UI & Progressive Web Apps (PWA)",
      "SEO-Optimized Headless CMS & Content Management Systems",
      "Bespoke Desktop & Mobile Enterprise Software"
    ],
    subserviceItems: [
      {
        title: "Corporate Web Design & Engineering",
        slug: "web-tasarim-yazilim",
        description: "Prestigious corporate web portals engineered for instantaneous load speeds, flawless mobile responsiveness, and top-tier SEO rankings.",
        keywords: ["corporate website", "custom web design", "fast web development"]
      },
      {
        title: "Custom Web Apps & SaaS Systems",
        slug: "ozel-yazilim-gelistirme",
        description: "Enterprise workflow automation portals, customer portals, custom quotation engines, and scalable multi-tenant SaaS platforms.",
        keywords: ["custom software development", "saas engineering", "web application agency"]
      },
      {
        title: "E-Commerce Architectures",
        slug: "e-ticaret-altyapisi",
        description: "High-conversion checkout flows, ERP/CRM/Logistics API integrations, and secure multi-currency payment infrastructure.",
        keywords: ["ecommerce website development", "custom online store", "payment gateway integration"]
      },
      {
        title: "Mobile Solutions & PWA",
        slug: "mobil-yazilim",
        description: "Cross-platform mobile applications, Progressive Web Apps (PWA), and mobile-centric digital interfaces.",
        keywords: ["mobile app development", "pwa web agency", "responsive web design"]
      }
    ],
    deliverables: [
      "Full Proprietary Source Code & Git Repository Handover",
      "Google PageSpeed 90+ Score, SEO & Accessibility Warranty",
      "Cross-Browser, Tablet & Mobile Responsive Precision",
      "Enterprise SSL, HSTS, Web Application Firewall & DDoS Hardening",
      "Intuitive Admin Dashboard & Structured Onboarding Training"
    ],
    process: [
      "Discovery & Information Architecture: Mapping user journeys, data schemas, and API integration boundaries.",
      "UI/UX Wireframing & Design: Prototyping interactive high-fidelity user interfaces in Figma.",
      "Frontend & Backend Engineering: Writing clean, type-safe code using modern React, Next.js, and TypeScript.",
      "SEO & Speed Auditing: Implementing JSON-LD Schema.org graphs, image optimization, and bundle splitting.",
      "Deployment & Verification: Stress-testing security and deploying seamlessly to high-speed cloud infrastructure."
    ],
    techSpecs: [
      {
        category: "Frontend Stack",
        items: ["React, Next.js, Vite, TypeScript", "Tailwind CSS Design System", "Framer Motion & Lucide Icons"]
      },
      {
        category: "Backend & Cloud",
        items: ["Node.js, Express, Python / FastAPI", "PostgreSQL, MySQL, Redis, Cloudflare Edge", "Hardened Linux / Apache / Nginx Infrastructure"]
      }
    ],
    articleSections: [
      {
        heading: "Why Custom-Coded Platforms Outperform Heavy CMS Templates",
        content: "Bloated template-based architectures carry hundreds of redundant scripts that severely degrade mobile PageSpeed and open security vulnerabilities. WE24 Agency crafts streamlined, type-safe web applications where every line of code serves a strategic purpose, loading in sub-second speeds and safeguarding your brand."
      }
    ],
    faqs: [
      {
        question: "How will our new website achieve high rankings on Google?",
        answer: "Every platform we engineer incorporates semantic HTML5 markup, automated JSON-LD Schema.org metadata, dynamic sitemaps, localized meta tags, and flawless Core Web Vitals as foundational standards."
      },
      {
        question: "Can our internal team easily update text and projects without coding?",
        answer: "Yes; we configure intuitive headless CMS solutions enabling non-technical team members to publish new projects, update copy, and manage media effortlessly."
      }
    ],
    icon: "Code"
  }
];
